import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UserModel } from '../../../../core/models/user/user.model';
import { Breakpoint, ResponsiveService } from '../../../../core/services/responsive.service';
import { UserService } from '../../../../core/services/user/user.service';
import { RowItem, TextItem } from '../../../../core/models/item.model';
import { TableService } from '../../../../core/services/table.service';
import { AccountService } from '../../../../core/services/user/account.service';
import { AuthenticateService } from '../../../../core/services/auth/authenticate.service';
import { TableColumn } from '../../../../core/models/table.model';
import { IspvisionService, VisionWs } from '../../../../core/services/ispvision.service';

@Component({
    selector: 'user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.less'],
})
export class UserListComponent implements OnInit, OnDestroy {
    public isSubAccounts: boolean = false;
    private subscription: Subscription | null = null;
    public importStart = false;

    button: TextItem = {
        type: 'text',
        icon: 'user-plus fa-lg',
        text: 'global_create',
        isButton: true,
        action: {
            type: 'click',
            click: () => {
                const subPage = this.router.url.match(/^\/admin\/users(\/*[0-9]{1,6}\/sub-accounts){0,1}$/);
                if (subPage?.[1] && this.userService.object) {
                    this.router.navigate([`admin/users/${this.userService.object.userId}/sub-accounts/new`]);
                } else {
                    this.router.navigate(['admin/users/new']);
                }
                return false;
            },
        },
    };

    importButton: TextItem = {
        type: 'text',
        icon: 'file-import',
        text: 'global_import',
        isButton: true,
        action: {
            type: 'click',
            click: () => {
                this.importDealers();
            },
        },
    };

    constructor(
        public tableService: TableService,
        public accountService: AccountService,
        private userService: UserService,
        private authenticate: AuthenticateService,
        private responsive: ResponsiveService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        public visionWs: IspvisionService
    ) {}

    async ngOnInit() {
        this.isSubAccounts = this.router.url.includes('/sub-accounts');
        if (this.isSubAccounts) {
            this.subscription = this.activatedRoute.params.subscribe(async (params) => {
                if (!this.userService.object || params.userId !== this.userService.object.userId) {
                    await this.userService.get(params.userId, '.page-content');
                }

                this.loadTable();
            });
        } else {
            this.loadTable();
        }
    }

    private async importDealers() {
        if (this.importStart) {
            return;
        }
        this.importStart = true;
        await this.visionWs.importDealers();
        location.reload();
    }

    public async loadTable() {
        const users = this.getSearchableUsers(
            this.isSubAccounts ? await this.userService.getAllSubAccounts() : await this.userService.getAll()
        );

        this.tableService.register(
            {
                paginator: true,
                breakpoint: { column: [Breakpoint.MD], row: [Breakpoint.MD] },
            },
            this.mapColumns.bind(this),
            this.mapUsersToTable.bind(this),
            users
        );
        this.tableService.addSearches([{ searchOn: 'searchText', accuracy: 100 }]);
    }

    ngOnDestroy() {
        this.tableService.destroyTable();
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    private getSearchableUsers(users: UserModel[]): UserModel[] {
        return users.map((user) => {
            user.searchText = this.buildSearchText(user);
            return user;
        });
    }

    private buildSearchText(user: UserModel): string {
        const searchableValues = [
            user.fullName,
            user.firstName,
            user.insertion,
            user.surname,
            user.companyName,
            user.email,
            user.ispCustomerId,
            user.verified ? 'geverifieerd verified ja actief' : 'niet geverifieerd unverified nee inactief',
        ];

        return searchableValues
            .filter((value) => value !== undefined && value !== null && value !== '')
            .map((value) => value.toString())
            .join(' ');
    }

    private mapColumns(): TableColumn[] {
        return [
            {
                item: { type: 'text', text: 'itemUser.company' },
                sortOn: 'companyName',
                width: 20,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 40,
                },
            },
            {
                item: { type: 'text', text: 'itemUser.email' },
                sortOn: 'email',
                width: 25,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 40,
                },
            },
            {
                item: { type: 'text', text: 'ISP klantnummer' },
                sortOn: 'ispCustomerId',
                width: 20,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    state: 'after',
                },
            },
            {
                item: { type: 'text', text: 'itemUser.verified' },
                position: 'center',
                sortOn: 'verified',
                width: 15,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    state: 'after',
                },
            },
            {
                item: { type: 'text', text: 'itemUser.actions' },
                position: 'center',
                width: 20,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    state: 'after',
                },
            },
        ];
    }

    public setButtonRoute() {}

    /**
     * Map users to table format
     *
     * @author    Lars Meeuwsen <lars@safira.nl>
     */
    private mapUsersToTable(user: Object): RowItem {
        const mappedUser = user as UserModel;

        return {
            type: 'row',
            expandable: {
                breakpoint: Breakpoint.MD,
                colspan: 2,
                gap: 10,
                closedHeight: 16.8,
                openHeight: 83.6,
            },
            items: [
                { type: 'text', fontWeight: 600, text: mappedUser.companyName, expansion: 'hidden' },
                {
                    type: 'group',
                    gap: 6,
                    expansion: 'expandable',
                    show: !this.responsive.isSize(Breakpoint.MD),
                    items: [
                        { type: 'text', fontWeight: 600, text: mappedUser.fullName },
                        {
                            type: 'text',
                            text: 'ISP klantnummer: ' + (mappedUser.ispCustomerId ?? '-'),
                        },
                        {
                            type: 'bool',
                            bool: mappedUser.verified,
                            icon: 'user-check fa-sm',
                            color: 'success',
                            falseIcon: 'user-slash fa-sm',
                            falseColor: 'default',
                        },
                    ],
                },
                { type: 'text', text: mappedUser.email, show: !this.responsive.isSize(Breakpoint.MD) },
                {
                    type: 'text',
                    text: mappedUser.email,
                    hover: true,
                    show: this.responsive.isSize(Breakpoint.MD),
                    action: {
                        type: 'anchor',
                        route: 'mailto:' + mappedUser.email,
                        target: 'top',
                    },
                },
                {
                    type: 'text',
                    text: (mappedUser.ispCustomerId ?? '-').toString(),
                    show: this.responsive.isSize(Breakpoint.MD),
                },
                {
                    type: 'bool',
                    bool: mappedUser.verified,
                    icon: 'user-check',
                    color: 'success',
                    falseIcon: 'user-xmark',
                    falseColor: 'error',
                    position: 'center',
                    show: this.responsive.isSize(Breakpoint.MD),
                },
                {
                    type: 'group',
                    position: this.responsive.isSize(Breakpoint.MD) ? 'center' : 'left',
                    expansion: 'expandable',
                    items: [
                        {
                            type: 'text',
                            hidden: mappedUser.adminAccount,
                            show: !mappedUser.adminAccount || this.responsive.isSize(Breakpoint.MD),
                            icon: 'sign-out',
                            iconBorder: true,
                            iconHover: true,
                            title: 'itemUser.loginAs',
                            action: {
                                type: 'popup',
                                title: {
                                    type: 'group',
                                    items: [
                                        {
                                            type: 'text',
                                            text: 'itemUser.loginAs',
                                        },
                                        { type: 'text', text: mappedUser.fullName },
                                    ],
                                },
                                form: {
                                    routeTo: '/',
                                    submitLabel: 'itemUser.submit_login_as',
                                    inputs: [
                                        {
                                            type: 'form',
                                            inputType: 'password',
                                            text: 'password',
                                            name: 'password',
                                            data: '',
                                            required: true,
                                        },
                                    ],
                                    submit: async (
                                        data: any,
                                        formData: Map<string, any>
                                    ): Promise<boolean> => {
                                        if (
                                            formData.get('password') === null ||
                                            formData.get('password').length <= 1
                                        ) {
                                            return false;
                                        }
                                        await this.authenticate.loginAs(
                                            mappedUser.userId,
                                            formData.get('password')
                                        );
                                        return true;
                                    },
                                },
                            },
                        },
                        {
                            type: 'text',
                            icon: 'pen',
                            iconHover: true,
                            iconBorder: true,
                            action: {
                                type: 'route',
                                route: './' + mappedUser.userId,
                            },
                            title: 'global_edit',
                        },
                        {
                            type: 'text',
                            icon: 'trash-alt',
                            iconBorder: true,
                            hidden: mappedUser.userId === this.accountService.object.userId,
                            iconHover: true,
                            title: 'global_delete',
                            action: {
                                type: 'popup',
                                title: {
                                    type: 'group',
                                    items: [
                                        {
                                            type: 'text',
                                            text: 'itemUser.delete',
                                        },
                                        { type: 'text', text: mappedUser.fullName },
                                    ],
                                },
                                form: {
                                    routeTo: './',
                                    submitLabel: 'itemUser.submit_delete',
                                    inputs: [
                                        {
                                            type: 'form',
                                            inputType: 'checkbox',
                                            text: 'itemUser.confirm_delete',
                                            name: 'confirm',
                                            data: false,
                                            required: true,
                                        },
                                    ],
                                    submit: async (data: any, formData: Map<string, any>) => {
                                        if (formData.get('confirm')) {
                                            await this.userService.delete(mappedUser);
                                            this.tableService.setObjects(
                                                this.getSearchableUsers(
                                                    this.isSubAccounts
                                                        ? await this.userService.getAllSubAccounts()
                                                        : await this.userService.getAll()
                                                )
                                            );
                                            return true;
                                        }
                                        return false;
                                    },
                                },
                            },
                        },
                    ],
                },
            ],
        };
    }
}
